import type { ApiResponse } from "../types/api";
import { getToken, logout } from "./auth";

// Default backend: FastAPI (8001). Override with REACT_APP_API_BASE.
// Production/Docker: set REACT_APP_API_BASE=/api (or empty) for same-origin; nginx proxies /api to backend.
// Development: unset or http://localhost:8001 so backend can run separately.
//
// LAN gotcha: if you open the frontend by IP (e.g. http://192.168.1.7:3000) but the
// API base is http://localhost:8001, the browser will either hit the *client's* localhost
// (other device) or be blocked by CORS. In development, treat loopback bases as "this host".
function resolveLoopbackBase(base: string): string {
  if (!base || base === "/api" || base === ".") return base;
  if (typeof window === "undefined") return base;
  if (!/^https?:\/\//i.test(base)) return base;
  try {
    const url = new URL(base);
    const isLoopback = url.hostname === "localhost" || url.hostname === "127.0.0.1";
    const currentHost = window.location.hostname;
    const currentIsLoopback = currentHost === "localhost" || currentHost === "127.0.0.1";
    if (isLoopback && currentHost && !currentIsLoopback) {
      url.hostname = currentHost;
      return url.toString().replace(/\/$/, "");
    }
  } catch {
    // ignore invalid URLs
  }
  return base;
}

const envBaseRaw = process.env.REACT_APP_API_BASE ?? "";
const envBase = resolveLoopbackBase(envBaseRaw);
const useSameOrigin = envBase === "" || envBase === "/api" || envBase === ".";
export const API_BASE = useSameOrigin ? "" : envBase.replace(/\/$/, "");

const fastApiEnv = process.env.REACT_APP_FASTAPI_PATIENT_BASE ?? "";
const useSameOriginFastApi = fastApiEnv === "" || fastApiEnv === "/api" || fastApiEnv === ".";
const isDevServer =
  typeof window !== "undefined" &&
  process.env.NODE_ENV === "development" &&
  window.location.port === "3000";
const devHost = typeof window !== "undefined" ? window.location.hostname : "localhost";
const fastApiBaseRaw = useSameOriginFastApi
  ? (isDevServer ? `http://${devHost}:8001` : "")
  : (fastApiEnv || "http://localhost:8001");
const FASTAPI_BASE = resolveLoopbackBase(fastApiBaseRaw);
// If FASTAPI_BASE isn't explicitly set, but API_BASE is an absolute URL (common in production),
// treat that as the FastAPI origin too. This avoids accidentally calling same-origin (Firebase SPA)
// for FastAPI endpoints like /health and /api/admin/*.
const FASTAPI_ORIGIN = FASTAPI_BASE || (/^https?:\/\//i.test(API_BASE) ? API_BASE : "");

// Log resolved base URL at app startup (dev only)
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  const effectiveBase = FASTAPI_ORIGIN || "(same-origin)";
  console.debug("API baseURL =", effectiveBase);
  if (API_BASE !== FASTAPI_BASE) console.debug("API baseURL (API_BASE) =", API_BASE || "(same-origin)");
}

// Migrated: All routing now handled by resolveApiUrl below.

/**
 * Centralized URL resolution.
 * Rule: anything starting with /api/ goes to FASTAPI_ORIGIN.
 * Only the handful of legacy unprefixed paths need special handling.
 */
function resolveApiUrl(endpoint: string): string {
  const [path, query] = endpoint.includes("?")
    ? [endpoint.slice(0, endpoint.indexOf("?")), endpoint.slice(endpoint.indexOf("?"))]
    : [endpoint, ""];

  // 1. Health
  if (path === "/health") return `${FASTAPI_ORIGIN || ""}/health${query}`;

  // 2. Auth — normalize to /api/auth/
  if (path === "/auth/login" || path === "/api/auth/login") {
    return `${FASTAPI_ORIGIN}/api/auth/login${query}`;
  }
  if (path.startsWith("/auth/")) {
    return `${FASTAPI_ORIGIN}/api/auth/${path.slice(6)}${query}`;
  }

  // 3. Any /api/* path → FastAPI origin (catch-all — no more per-namespace whitelisting)
  if (path.startsWith("/api/")) {
    return `${FASTAPI_ORIGIN}${path}${query}`;
  }

  // 4. Legacy unprefixed Patient/Visit paths → rewrite to /api/
  if (path === "/patients") return `${FASTAPI_ORIGIN}/api/patients/${query}`;
  if (path.startsWith("/patients/")) return `${FASTAPI_ORIGIN}/api/patients${path.slice(9)}${query}`;
  if (path.startsWith("/manager/")) return `${FASTAPI_ORIGIN}/api${path}${query}`;

  // Default: same-origin or API_BASE
  return `${API_BASE}${endpoint}`;
}

export function toApiUrl(endpoint: string): string {
  if (!endpoint.startsWith("/")) {
    throw new Error(`API endpoint must start with "/": ${endpoint}`);
  }
  return resolveApiUrl(endpoint);
}


/** Full URL for backend health check (used for offline detection when frontend is on Firebase). */
export function getHealthCheckUrl(): string {
  const base = FASTAPI_ORIGIN || API_BASE || "";
  return base ? `${base.replace(/\/$/, "")}/api/health` : "/api/health";
}

// ========================================
// COMMON RESPONSE HANDLER (SAFE)
// ========================================
async function handleResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") || "";
  let payload: unknown = null;

  // Read body ONCE
  if (response.status !== 204) {
    payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();
  }

  // Handle errors (attach status and body so callers can handle 409 etc.)
  if (!response.ok) {
    const msg =
      typeof payload === "string"
        ? payload || `HTTP ${response.status}`
        : (payload as { detail?: string | string[]; error?: string; message?: string })?.detail != null
          ? (Array.isArray((payload as { detail?: string[] }).detail)
            ? (payload as { detail: string[] }).detail.join(", ")
            : String((payload as { detail: string }).detail))
          : (payload as { error?: string; message?: string })?.error ||
          (payload as { error?: string; message?: string })?.message ||
          `HTTP ${response.status}`;
    const err = new Error(msg) as Error & { status: number; body?: unknown };
    err.status = response.status;
    err.body = payload;
    throw err;
  }

  return payload;
}

// ========================================
// COMMON FETCH WRAPPER
// ========================================
async function apiFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<unknown> {
  if (!endpoint.startsWith("/")) {
    throw new Error(`API endpoint must start with "/": ${endpoint}`);
  }

  const url = resolveApiUrl(endpoint);

  const headers = new Headers(options.headers);
  const token = getToken();

  // Extra safeguard: never send literal string "null" or "undefined" (Step 37)
  if (token && token !== "null" && token !== "undefined") {
    headers.set("Authorization", `Bearer ${token}`);
  }

  try {
    const response = await fetch(url, {
      credentials: "include",
      ...options,
      headers,
    });
    const payload = await handleResponse(response);
    return payload;
  } catch (error) {
    const err = error as Error & { status?: number };
    const status = err.status;
    if (status === 401) {
      // Don't redirect on login attempt; allow UI to show "Incorrect password".
      if (endpoint === "/auth/login" || endpoint === "/api/auth/login") {
        throw error;
      }
      // Session expired or invalid — redirect to login with a friendly message.
      logout();
      try {
        sessionStorage.setItem("auth:expired", "1");
      } catch {
        // ignore storage errors
      }
      const path = window.location.pathname;
      const role =
        path.startsWith("/reception")
          ? "reception"
          : path.startsWith("/doctor")
            ? "doctor"
            : path.startsWith("/pharmacy")
              ? "pharmacy"
              : path.startsWith("/manager")
                ? "manager"
                : path.startsWith("/admin")
                  ? "admin"
                  : "";
      const redirect = encodeURIComponent(path + window.location.search);
      const roleParam = role ? `role=${encodeURIComponent(role)}&` : "";
      window.location.replace(`/login?${roleParam}reason=expired&redirect=${redirect}`);
    } else if (status === 409) {
      // Let caller handle 409 (e.g. "Master data already loaded")
    } else if (status === 502 || status === 503 || status === 504) {
      // Backend unavailable — avoid dumping HTML into console
      console.warn("API", status, ": Backend unavailable —", endpoint);
    } else {
      console.error("API Error:", endpoint, error);
    }
    throw error;
  }
}

// ========================================
// GET (typed)
// ========================================
export async function apiGet<T = unknown>(endpoint: string): Promise<T> {
  const result = await apiFetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Handle both raw data and wrapped { success, data, message } responses
  if (
    result !== null &&
    typeof result === "object" &&
    "success" in result &&
    "data" in result
  ) {
    const wrapped = result as ApiResponse<T>;
    return wrapped.data;
  }

  // Return raw data as-is (backward compatible)
  return result as T;
}

// ========================================
// POST (typed)
// ========================================
export async function apiPost<T = unknown>(
  endpoint: string,
  data: unknown
): Promise<T> {
  const result = await apiFetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  return result as T;
}

// ========================================
// PUT (typed)
// ========================================
export async function apiPut<T = unknown>(
  endpoint: string,
  data: unknown
): Promise<T> {
  const result = await apiFetch(endpoint, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  return result as T;
}

// ========================================
// PATCH
// ========================================
export const apiPatch = (endpoint: string, data: unknown): Promise<unknown> =>
  apiFetch(endpoint, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

// ========================================
// DELETE (typed)
// ========================================
export async function apiDelete<T = unknown>(endpoint: string): Promise<T> {
  const result = await apiFetch(endpoint, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
  return result as T;
}

// ========================================
// UPLOAD (Multipart/Form-Data)
// ========================================
export const apiUpload = (
  endpoint: string,
  formData: FormData
): Promise<unknown> =>
  apiFetch(endpoint, {
    method: "POST",
    body: formData, // browser sets boundary
  });

// ========================================
// SPECIALIZED HELPERS
// ========================================
/** Load procedure catalog from FastAPI (procedure_master). Step 30. */
export const getProcedureCatalog = (): Promise<unknown> =>
  apiGet("/api/procedures/catalog");

export const fetchProceduresList = (q = ""): Promise<unknown> =>
  apiGet(`/api/doctor/procedures?q=${encodeURIComponent(q)}`);
